import React, { useEffect, useState, useRef } from "react";
import "./AnimatedPhotoWall.css";
import "./FloatingPhotoCircle.css";
import { getImageUrl } from "../config/assets";

export interface FloatingPhotoCircleProps {
  id: string;
  photo_url: string;
  name: string;
  field_1?: string;  // Campo genérico 1 (ex: frase, descrição)
  isNew: boolean;
  // Configurações de movimento
  speed?: number; // pixels por segundo (padrão: 12)
  minDistance?: number; // distância mínima para considerar que chegou no alvo (padrão: 2)
  // Configurações de posição inicial
  initialPosition?: { left: number; top: number };
  // Configurações de área de movimento
  movementBounds?: {
    minX?: number;
    maxX?: number;
    minY?: number;
    maxY?: number;
    // Área proibida (ex: logo central)
    forbiddenArea?: {
      left: number;
      right: number;
      top: number;
      bottom: number;
    };
    // Área proibida adicional (ex: logo canto)
    forbiddenAreaAche?: {
      left: number;
      right: number;
      top: number;
      bottom: number;
    };
    // Área proibida do texto central
    forbiddenAreaText?: {
      left: number;
      right: number;
      top: number;
      bottom: number;
    };
  };
  // Configurações visuais
  className?: string;
  style?: React.CSSProperties;
  // Tamanho inicial em porcentagem da tela (será calculado automaticamente)
  initialSizePercentage?: number;
  // Controle de transições de entrada/saída
  shouldEnter?: boolean;
  shouldExit?: boolean;
  onExitComplete?: (id: string) => void;
}

const getRandomTarget = (bounds?: FloatingPhotoCircleProps['movementBounds'], bubbleSize: number = 140) => {
  // Garantir que os elementos fiquem completamente dentro da área visível
  const bubbleWidth = bubbleSize;
  const bubbleHeight = bubbleSize; // Mantendo quadrado
  
  // Verificar se o elemento é muito grande para a tela
  if (bubbleSize > window.innerWidth || bubbleSize > window.innerHeight) {
    console.warn(`getRandomTarget: Elemento muito grande (${bubbleSize}px) para a tela (${window.innerWidth}x${window.innerHeight})`);
    // Retornar posição central segura
    return {
      left: Math.max(0, (window.innerWidth - bubbleSize) / 2),
      top: Math.max(0, (window.innerHeight - bubbleSize) / 2)
    };
  }
  
  const minX = bounds?.minX || 0;
  const maxX = bounds?.maxX || (window.innerWidth - bubbleWidth);
  const minY = bounds?.minY || 0;
  const maxY = bounds?.maxY || (window.innerHeight - bubbleHeight);

  // Garantir que os limites não sejam negativos e não ultrapassem a tela
  const safeMinX = Math.max(0, minX);
  const safeMaxX = Math.min(window.innerWidth - bubbleWidth, maxX);
  const safeMinY = Math.max(0, minY);
  const safeMaxY = Math.min(window.innerHeight - bubbleHeight, maxY);

  // Função para verificar se uma posição está na área proibida
  const isInForbiddenArea = (x: number, y: number) => {
    if (!bounds?.forbiddenArea && !bounds?.forbiddenAreaAche && !bounds?.forbiddenAreaText) return false;
    
    // Verificar área proibida principal (logo ASCME central)
    if (bounds?.forbiddenArea) {
      const forbidden = bounds.forbiddenArea;
      const inMainForbiddenArea = (
        x + bubbleWidth > forbidden.left &&
        x < forbidden.right &&
        y + bubbleHeight > forbidden.top &&
        y < forbidden.bottom
      );
      if (inMainForbiddenArea) return true;
    }
    
    // Verificar área proibida adicional (logo ACHE canto)
    if (bounds?.forbiddenAreaAche) {
      const forbiddenAche = bounds.forbiddenAreaAche;
      const inAcheForbiddenArea = (
        x + bubbleWidth > forbiddenAche.left &&
        x < forbiddenAche.right &&
        y + bubbleHeight > forbiddenAche.top &&
        y < forbiddenAche.bottom
      );
      if (inAcheForbiddenArea) return true;
    }
    
    // Verificar área proibida do texto central
    if (bounds?.forbiddenAreaText) {
      const forbiddenText = bounds.forbiddenAreaText;
      const inTextForbiddenArea = (
        x + bubbleWidth > forbiddenText.left &&
        x < forbiddenText.right &&
        y + bubbleHeight > forbiddenText.top &&
        y < forbiddenText.bottom
      );
      if (inTextForbiddenArea) return true;
    }
    
    return false;
  };

  // Tentar encontrar uma posição válida
  let attempts = 0;
  let left, top;
  
  do {
    left = Math.max(safeMinX, Math.min(safeMaxX, Math.random() * (safeMaxX - safeMinX) + safeMinX));
    top = Math.max(safeMinY, Math.min(safeMaxY, Math.random() * (safeMaxY - safeMinY) + safeMinY));
    attempts++;
  } while (isInForbiddenArea(left, top) && attempts < 50);

  // Se não conseguir evitar a área proibida, escolher uma área segura
  if (attempts >= 50 && (bounds?.forbiddenArea || bounds?.forbiddenAreaAche || bounds?.forbiddenAreaText)) {
    const forbidden = bounds?.forbiddenArea;
    const forbiddenAche = bounds?.forbiddenAreaAche;
    const forbiddenText = bounds?.forbiddenAreaText;
    
    // Dividir tela em áreas seguras considerando todas as áreas proibidas
    const areas = [];
    
    // Área esquerda (evita logo central)
    if (forbidden) {
      areas.push({ 
        minX: safeMinX, 
        maxX: Math.max(safeMinX, forbidden.left - bubbleWidth), 
        minY: safeMinY, 
        maxY: safeMaxY 
      });
    }
    
    // Área direita (evita logo central)
    if (forbidden) {
      areas.push({ 
        minX: Math.min(safeMaxX, forbidden.right), 
        maxX: safeMaxX, 
        minY: safeMinY, 
        maxY: safeMaxY 
      });
    }
    
    // Área superior (evita ambas as logos)
    let topMaxY = safeMaxY;
    if (forbidden) topMaxY = Math.min(topMaxY, forbidden.top - bubbleHeight);
    if (forbiddenAche) topMaxY = Math.min(topMaxY, forbiddenAche.top - bubbleHeight);
    areas.push({ 
      minX: safeMinX, 
      maxX: safeMaxX, 
      minY: safeMinY, 
      maxY: Math.max(safeMinY, topMaxY)
    });
    
    // Área inferior (evita logo central)
    if (forbidden) {
      areas.push({ 
        minX: safeMinX, 
        maxX: safeMaxX, 
        minY: Math.min(safeMaxY, forbidden.bottom), 
        maxY: safeMaxY 
      });
    }
    
    // Área inferior esquerda (evita logo ACHE)
    if (forbiddenAche) {
      areas.push({ 
        minX: safeMinX, 
        maxX: Math.max(safeMinX, forbiddenAche.left - bubbleWidth), 
        minY: Math.min(safeMaxY, forbiddenAche.bottom), 
        maxY: safeMaxY 
      });
    }

    const validAreas = areas.filter(area => area.maxX > area.minX && area.maxY > area.minY);

    if (validAreas.length > 0) {
      const area = validAreas[Math.floor(Math.random() * validAreas.length)];
      left = Math.random() * (area.maxX - area.minX) + area.minX;
      top = Math.random() * (area.maxY - area.minY) + area.minY;
    }
  }

  return { left, top };
};

// Função para determinar posição de entrada aleatória (fora da tela)
const getEntryPosition = (bubbleSize: number = 140) => {
  const side = Math.floor(Math.random() * 4);
  
  switch (side) {
    case 0: // Entrada por cima
      return {
        left: Math.random() * (window.innerWidth - bubbleSize),
        top: -bubbleSize - 100
      };
    case 1: // Entrada pela direita
      return {
        left: window.innerWidth + 100,
        top: Math.random() * (window.innerHeight - bubbleSize)
      };
    case 2: // Entrada por baixo
      return {
        left: Math.random() * (window.innerWidth - bubbleSize),
        top: window.innerHeight + 100
      };
    case 3: // Entrada pela esquerda
    default:
      return {
        left: -bubbleSize - 100,
        top: Math.random() * (window.innerHeight - bubbleSize)
      };
  }
};

// Função para determinar posição de saída mais próxima
const getExitTarget = (currentPos: { left: number; top: number }, bubbleSize: number = 140) => {
  const { left, top } = currentPos;
  
  // Calcular distâncias até cada borda
  const distToLeft = left;
  const distToTop = top;
  const distToRight = window.innerWidth - left - bubbleSize;
  const distToBottom = window.innerHeight - top - bubbleSize;
  
  // Encontrar a borda mais próxima
  const minDist = Math.min(distToLeft, distToTop, distToRight, distToBottom);
  
  // Gerar posição de saída pela borda mais próxima
  if (minDist === distToLeft) {
    return { left: -bubbleSize - 100, top }; // Sair pela esquerda
  } else if (minDist === distToTop) {
    return { left, top: -bubbleSize - 100 }; // Sair pelo topo
  } else if (minDist === distToRight) {
    return { left: window.innerWidth + 100, top }; // Sair pela direita
  } else {
    return { left, top: window.innerHeight + 100 }; // Sair por baixo
  }
};

const FloatingPhotoCircle: React.FC<FloatingPhotoCircleProps> = ({
  id,
  photo_url,
  name,
  field_1 = "",
  isNew = false,
  speed = 12,
  minDistance = 2,
  initialPosition,
  movementBounds,
  className = "",
  style = {},
  initialSizePercentage,
  shouldEnter = false,
  shouldExit = false,
  onExitComplete
}) => {
  // Gerar tamanho inicial aleatório entre 7% e 15% da altura da tela
  const getInitialSize = () => {
    if (isNew) return 20; // 20% da altura para elementos novos (reduzido de 40%)
    if (initialSizePercentage) return initialSizePercentage;
    return Math.random() * 10 + 10; // 10-20% para elementos normais
  };

  const getNormalSize = () => {
    if (initialSizePercentage) return initialSizePercentage;
    return Math.random() * 10 + 10; // 10-20% para o tamanho normal
  };

  // Estados para posição e tamanho
  const initialSize = getInitialSize();
  const initialBubbleSize = Math.round((initialSize / 100) * window.innerHeight);
  
  // Determinar posição inicial sempre dentro da área visível
  const getInitialPosition = () => {
    if (initialPosition) return initialPosition;
    // Sempre iniciar dentro da área visível, não mais de fora da tela
    return getRandomTarget(movementBounds, initialBubbleSize);
  };
  
  const [position, setPosition] = useState<{ left: number; top: number }>(getInitialPosition());
  const [currentSizePercentage, setCurrentSizePercentage] = useState(initialSize);
  const [isSizeTransitioning, setIsSizeTransitioning] = useState(false);
  
  // Estados de transição (entrada/saída) - modificado para sempre estar visível
  const [isEntering, setIsEntering] = useState(shouldEnter);
  const [isExiting, setIsExiting] = useState(false);
  const [opacity, setOpacity] = useState(shouldEnter ? 0 : 1); // Começar com opacidade baseada na entrada
  const [isTransitioning, setIsTransitioning] = useState(false);
  
  // Estados para controle de carregamento da imagem
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  
  // Refs para movimento
  const targetRef = useRef<{ left: number; top: number }>(getRandomTarget(movementBounds, initialBubbleSize));
  const hasExitedRef = useRef(false);

  // useEffect para garantir que o alvo seja definido corretamente na inicialização
  useEffect(() => {
    const bubbleSize = Math.round((currentSizePercentage / 100) * window.innerHeight);
    targetRef.current = getRandomTarget(movementBounds, bubbleSize);
  }, []); // Executar apenas uma vez na inicialização
  
  // Refs para controle de tamanho
  const targetSizeRef = useRef(currentSizePercentage);
  const sizeTransitionRef = useRef({
    isTransitioning: false,
    transitionStartTime: 0,
    startSize: currentSizePercentage,
    duration: 2000,
    stayDuration: 0,
    nextChangeTime: Date.now() + 20000 // 5-20s
  });

  // useEffect para gerenciar transições de entrada
  useEffect(() => {
    if (shouldEnter && isEntering) {
      // Definir posição alvo dentro da tela imediatamente
      const currentBubbleSize = Math.round((currentSizePercentage / 100) * window.innerHeight);
      targetRef.current = getRandomTarget(movementBounds, currentBubbleSize);
      
      // Iniciar transição suave de entrada após a imagem carregar
      if (imageLoaded && !imageError) {
        setIsTransitioning(true);
        
        const startTime = Date.now();
        const duration = 2000; // 2 segundos
        
        const fadeIn = () => {
          const elapsed = Date.now() - startTime;
          const progress = Math.min(elapsed / duration, 1);
          
          // Interpolação linear de opacidade de 0 para 1
          const newOpacity = progress;
          setOpacity(newOpacity);
          
          if (progress < 1) {
            requestAnimationFrame(fadeIn);
          } else {
            // Transição completa
            setIsTransitioning(false);
            setIsEntering(false);
          }
        };
        
        requestAnimationFrame(fadeIn);
      }
    }
  }, [shouldEnter, isEntering, movementBounds, currentSizePercentage, imageLoaded, imageError]);

  // useEffect para gerenciar transição de tamanho para elementos novos
  useEffect(() => {
    if (isNew && imageLoaded && !imageError) {
      // Aguardar 2 segundos e depois reduzir para o tamanho normal
      const timer = setTimeout(() => {
        setIsSizeTransitioning(true);
        
        const startTime = Date.now();
        const duration = 2000; // 2 segundos para a transição de tamanho
        const startSize = currentSizePercentage;
        const targetSize = getNormalSize();
        
        const resizeAnimation = () => {
          const elapsed = Date.now() - startTime;
          const progress = Math.min(elapsed / duration, 1);
          
          // Interpolação suave usando easing out
          const easeProgress = 1 - Math.pow(1 - progress, 3);
          const newSize = startSize + (targetSize - startSize) * easeProgress;
          
          setCurrentSizePercentage(newSize);
          
          if (progress < 1) {
            requestAnimationFrame(resizeAnimation);
          } else {
            // Transição de tamanho completa
            setIsSizeTransitioning(false);
          }
        };
        
        requestAnimationFrame(resizeAnimation);
      }, 2000); // Aguardar 2 segundos antes de iniciar a redução

      return () => clearTimeout(timer);
    }
  }, [isNew, imageLoaded, imageError, currentSizePercentage]);

  // useEffect para atualizar alvo de movimento quando tamanho mudar
  useEffect(() => {
    if (isSizeTransitioning) {
      const bubbleSize = Math.round((currentSizePercentage / 100) * window.innerHeight);
      // Recalcular alvo com novo tamanho se necessário
      const currentTarget = targetRef.current;
      
      // Se o alvo atual está fora dos limites com o novo tamanho, gerar novo alvo
      const maxX = window.innerWidth - bubbleSize;
      const maxY = window.innerHeight - bubbleSize;
      
      if (currentTarget.left > maxX || currentTarget.top > maxY) {
        const newTarget = getRandomTarget(movementBounds, bubbleSize);
        targetRef.current = newTarget;
      }
    }
  }, [currentSizePercentage, isSizeTransitioning, movementBounds]);

  // useEffect para controlar visibilidade baseada no carregamento da imagem
  useEffect(() => {
    if (imageLoaded && !imageError) {
      // Para elementos novos, sempre manter opacidade 1 após carregamento
      if (isNew) {
        setOpacity(1);
        return;
      }
      
      // Para elementos normais, seguir a lógica original
      if (!isTransitioning && !shouldEnter && !shouldExit) {
        setOpacity(1);
      }
      if (shouldEnter && isEntering && !isTransitioning) {
        setOpacity(0);
      }
    } else if (imageError) {
      setOpacity(0);
    }
  }, [imageLoaded, imageError, shouldEnter, isEntering, shouldExit, isTransitioning, isNew]);

  // Handlers para carregamento da imagem
  const handleImageLoad = () => {
    setImageLoaded(true);
    setImageError(false);
  };

  const handleImageError = () => {
    setImageError(true);
    setImageLoaded(false);
  };

  // useEffect para gerenciar transições de saída - modificado para manter na área visível
  useEffect(() => {
    if (shouldExit && !isExiting && !hasExitedRef.current) {
      setIsExiting(true);
      setIsTransitioning(true);
      
      // Iniciar transição suave de opacidade para 0 em 2 segundos
      const startTime = Date.now();
      const duration = 2000; // 2 segundos
      const initialOpacity = opacity;
      
      const fadeOut = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        // Interpolação linear de opacidade
        const newOpacity = initialOpacity * (1 - progress);
        setOpacity(newOpacity);
        
        if (progress < 1) {
          requestAnimationFrame(fadeOut);
        } else {
          // Transição completa
          setIsTransitioning(false);
          if (onExitComplete && !hasExitedRef.current) {
            hasExitedRef.current = true;
            onExitComplete(id);
          }
        }
      };
      
      requestAnimationFrame(fadeOut);
      
      return;
      
      // Chamar callback após tempo suficiente para sair
      setTimeout(() => {
        if (!hasExitedRef.current && onExitComplete) {
          hasExitedRef.current = true;
          onExitComplete(id);
        }
      }, 4000);
    }
  }, [shouldExit, position, id, onExitComplete, currentSizePercentage]);

  useEffect(() => {
    // Sistema de movimento simples e confiável
    const moveInterval = setInterval(() => {
      setPosition(prevPos => {
        const bubbleSize = Math.round((currentSizePercentage / 100) * window.innerHeight);
        const target = targetRef.current;
        
        const dx = target.left - prevPos.left;
        const dy = target.top - prevPos.top;
        const dist = Math.sqrt(dx * dx + dy * dy);

        // Debug ocasional apenas para elementos novos se necessário
        if (isNew && Math.random() < 0.001) { // Log muito raro
          console.log(`${id} (isNew): pos(${Math.round(prevPos.left)},${Math.round(prevPos.top)}) dist:${Math.round(dist)} opacity:${opacity}`);
        }

        // Se chegou perto do alvo, gerar novo alvo (exceto se estiver saindo)
        if (dist < minDistance && !isExiting) {
          const newTarget = getRandomTarget(movementBounds, bubbleSize);
          targetRef.current = newTarget;
          return prevPos;
        }

        // Para elementos isNew, usar movimento simplificado
        if (isNew) {
          const moveStep = 3;
          const moveX = dx > 0 ? Math.min(moveStep, dx) : Math.max(-moveStep, dx);
          const moveY = dy > 0 ? Math.min(moveStep, dy) : Math.max(-moveStep, dy);
          
          const newLeft = Math.round(prevPos.left + moveX);
          const newTop = Math.round(prevPos.top + moveY);
          
          // Garantir que elementos grandes não saiam da tela
          const maxLeft = Math.max(0, window.innerWidth - bubbleSize);
          const maxTop = Math.max(0, window.innerHeight - bubbleSize);
          
          const clampedLeft = Math.max(0, Math.min(maxLeft, newLeft));
          const clampedTop = Math.max(0, Math.min(maxTop, newTop));
          
          return { left: clampedLeft, top: clampedTop };
        }

        // Movimento normal para elementos não-novos
        const moveStep = 3;
        const moveX = dist > 0 ? (dx / dist) * Math.min(moveStep, dist) : 0;
        const moveY = dist > 0 ? (dy / dist) * Math.min(moveStep, dist) : 0;
        
        const newLeft = Math.round(prevPos.left + moveX);
        const newTop = Math.round(prevPos.top + moveY);
        
        // Se está saindo, não clampar para permitir sair da tela
        if (isExiting) {
          return { left: newLeft, top: newTop };
        }
        
        // Se não está saindo, clampar dentro da tela
        let clampedLeft = Math.max(0, Math.min(window.innerWidth - bubbleSize, newLeft));
        let clampedTop = Math.max(0, Math.min(window.innerHeight - bubbleSize, newTop));

        // Verificar se a nova posição está na área proibida
        const isInForbiddenArea = (x: number, y: number) => {
          if (!movementBounds?.forbiddenArea && !movementBounds?.forbiddenAreaAche && !movementBounds?.forbiddenAreaText) return false;
          
          // Verificar área proibida principal (logo ASCME central)
          if (movementBounds?.forbiddenArea) {
            const forbidden = movementBounds.forbiddenArea;
            const inMainForbiddenArea = (
              x + bubbleSize > forbidden.left &&
              x < forbidden.right &&
              y + bubbleSize > forbidden.top &&
              y < forbidden.bottom
            );
            if (inMainForbiddenArea) return true;
          }
          
          // Verificar área proibida adicional (logo ACHE canto)
          if (movementBounds?.forbiddenAreaAche) {
            const forbiddenAche = movementBounds.forbiddenAreaAche;
            const inAcheForbiddenArea = (
              x + bubbleSize > forbiddenAche.left &&
              x < forbiddenAche.right &&
              y + bubbleSize > forbiddenAche.top &&
              y < forbiddenAche.bottom
            );
            if (inAcheForbiddenArea) return true;
          }
          
          // Verificar área proibida do texto central
          if (movementBounds?.forbiddenAreaText) {
            const forbiddenText = movementBounds.forbiddenAreaText;
            const inTextForbiddenArea = (
              x + bubbleSize > forbiddenText.left &&
              x < forbiddenText.right &&
              y + bubbleSize > forbiddenText.top &&
              y < forbiddenText.bottom
            );
            if (inTextForbiddenArea) return true;
          }
          
          return false;
        };

        // Se está entrando na área proibida, gerar novo alvo
        if (isInForbiddenArea(clampedLeft, clampedTop)) {
          targetRef.current = getRandomTarget(movementBounds, bubbleSize);
          return prevPos; // Manter posição atual
        }

        return { left: clampedLeft, top: clampedTop };
      });
    }, 50);

    return () => {
      clearInterval(moveInterval);
    };
  }, [minDistance, movementBounds, isExiting]); // Dependências simplificadas

  // useEffect separado para variação de tamanho - desabilitado para elementos novos
  useEffect(() => {
    // Não aplicar variações automáticas de tamanho para elementos novos
    if (isNew) return;
    
    const sizeInterval = setInterval(() => {
      const sizeTransition = sizeTransitionRef.current;
      const now = Date.now();
      
      // Verificar se é hora de começar uma nova transição de tamanho
      if (!sizeTransition.isTransitioning && now >= sizeTransition.nextChangeTime) {
        const newTargetSize = Math.random() * 8 + 7; // 7-15%
        const stayDuration = 4000; // 4s no novo tamanho
        
        // Configurar transição
        sizeTransition.isTransitioning = true;
        sizeTransition.transitionStartTime = now;
        sizeTransition.startSize = currentSizePercentage;
        sizeTransition.duration = 1000; // 1s para transição
        sizeTransition.stayDuration = stayDuration;
        targetSizeRef.current = newTargetSize;
      }
      
      // Aplicar transição de tamanho se estiver em andamento
      if (sizeTransition.isTransitioning) {
        const elapsed = now - sizeTransition.transitionStartTime;
        
        if (elapsed < sizeTransition.duration) {
          // Fase de transição
          const progress = elapsed / sizeTransition.duration;
          const easedProgress = progress < 0.5 
            ? 2 * progress * progress 
            : 1 - Math.pow(-2 * progress + 2, 3) / 2;
          
          const newSize = sizeTransition.startSize + 
            (targetSizeRef.current - sizeTransition.startSize) * easedProgress;
          
          setCurrentSizePercentage(newSize);
        } else if (elapsed < sizeTransition.duration + sizeTransition.stayDuration) {
          // Fase de permanência no tamanho alvo
          setCurrentSizePercentage(targetSizeRef.current);
        } else {
          // Fim da transição, programar próxima mudança
          sizeTransition.isTransitioning = false;
          sizeTransition.nextChangeTime = now + 4000; // 4s até próxima mudança
        }
      }
    }, 100);

    return () => {
      clearInterval(sizeInterval);
    };
  }, [currentSizePercentage, isNew]);

  // useEffect para debug de opacidade para elementos isNew
  useEffect(() => {
    if (isNew) {
      console.log(`${id} (isNew): Opacity changed to ${opacity}, imageLoaded: ${imageLoaded}, imageError: ${imageError}, isTransitioning: ${isTransitioning}`);
    }
  }, [opacity, isNew, id, imageLoaded, imageError, isTransitioning]);

  // Calcular tamanho atual em pixels baseado na porcentagem
  const bubbleSize = Math.round((currentSizePercentage / 100) * window.innerHeight);

  // Se a imagem falhou ao carregar, não renderizar o componente
  if (imageError) {
    return null;
  }

  return (
    <div
      className={`floating-circle ${className}`}
      style={{
        position: "absolute",
        left: position.left,
        top: position.top,
        width: `${bubbleSize}px`,
        height: `${bubbleSize}px`,
        zIndex: 2,
        pointerEvents: "none",
        opacity: opacity,
        // Desabilitar transições CSS para elementos novos para evitar conflitos
        transition: (isTransitioning || isSizeTransitioning || isNew) ? "none" : "opacity 0.3s ease-in-out, width 0.3s ease-in-out, height 0.3s ease-in-out",
        ...style
      }}
    >
      <div className="circle-content" style={{ position: "relative", width: "100%", height: "100%" }}>
        {/* Foto de fundo com clip circular */}
        <div 
          className="circle-photo-background"
          style={{
            position: "absolute",
            width: "100%",
            height: "100%",
            borderRadius: "50%", // Criar máscara circular
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}
        >
          <img 
            src={photo_url} 
            alt={name}
            onLoad={handleImageLoad}
            onError={handleImageError}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover", // Cobrir toda a área circular
            objectPosition: "center",
            borderRadius: "50%",
            WebkitMask: "radial-gradient(circle, black 50%, transparent 70%)",
            mask: "radial-gradient(circle, black 50%, transparent 70%)"              
            }}
          />
        </div>
        
        {/* Moldura do círculo por cima */}
        {/* <img 
          src={getImageUrl('frame_ache_2.png')} 
          alt="Moldura do círculo"
          className="circle-frame"
          style={{
            position: "absolute",
            top: "10%",
            left: "0%",
            width: "100%",
            height: "100%",
            objectFit: "contain",
            pointerEvents: "none",
            zIndex: 1
          }}
        /> */}
      </div>
    </div>
  );
};

export default FloatingPhotoCircle;
